<template>
  <div class="blog-page">
    <!-- Hero Section -->
    <div class="blog-hero">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-8 mx-auto text-center">
            <h1 class="blog-hero-title"> Blogs</h1>
            <p class="blog-hero-subtitle">Insights, tips, and stories from the franchise world</p>
            
            <!-- Search Bar -->
            <div class="search-container">
              <div class="input-group">
                <input 
                  v-model="searchQuery" 
                  type="text" 
                  class="form-control search-input" 
                  placeholder="Search blogs..."
                  @input="handleSearch"
                />
                <button class="btn btn-primary search-btn" @click="handleSearch">
                  <i class="fa fa-search"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Category Filter -->
    <div class="category-filter">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <div class="category-buttons">
              <button 
                @click="setCategory(null)" 
                :class="['category-btn', { active: selectedCategory === null }]"
              >
                All
              </button>
              <button 
                v-for="category in categories" 
                :key="category"
                @click="setCategory(category)" 
                :class="['category-btn', { active: selectedCategory === category }]"
              >
                {{ category }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Blog Content -->
    <div class="blog-content">
      <div class="container">
        <!-- Loading State -->
        <div v-if="blogStore.loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3">Loading blog posts...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="blogStore.error" class="alert alert-warning" role="alert">
          <h4 class="alert-heading">Unable to load blogs</h4>
          <p>{{ blogStore.error }}</p>
          <button @click="loadBlogs" class="btn btn-primary">Try Again</button>
        </div>

        <!-- Blog Grid -->
        <div v-else>
          <div v-if="filteredBlogs.length === 0" class="text-center py-5">
            <h3>No blogs found</h3>
            <p v-if="searchQuery">Try adjusting your search terms</p>
            <p v-else-if="selectedCategory">No blogs found in this category</p>
            <button @click="clearFilters" class="btn btn-primary mt-3">Clear Filters</button>
          </div>

          <div v-else class="row">
            <div 
              v-for="blog in filteredBlogs" 
              :key="blog.id" 
              class="col-lg-4 col-md-6 mb-4"
            >
              <BlogCard :blog="blog" />
            </div>
          </div>

          <!-- Load More Button -->
          <div v-if="hasMoreBlogs" class="text-center mt-5">
            <button @click="loadMore" class="btn btn-outline-primary btn-lg">
              Load More Blogs
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useBlogStore } from '../stores/blogStore'
import BlogCard from '../components/BlogCard.vue'

const blogStore = useBlogStore()
const searchQuery = ref('')
const selectedCategory = ref(null)
const displayedCount = ref(9)
const sortBy = ref('newest')

const categories = computed(() => {
  const cats = [...new Set(blogStore.blogs.map(blog => blog.category))]
  return cats.filter(cat => cat) // Remove null/undefined categories
})

const filteredBlogs = computed(() => {
  let blogs = blogStore.blogs

  // Filter by category
  if (selectedCategory.value) {
    blogs = blogs.filter(blog => blog.category === selectedCategory.value)
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    blogs = blogs.filter(blog => 
      blog.title.toLowerCase().includes(query) ||
      blog.summary.toLowerCase().includes(query) ||
      blog.content?.toLowerCase().includes(query) ||
      blog.category?.toLowerCase().includes(query)
    )
  }

  return blogs.slice(0, displayedCount.value)
})

const hasMoreBlogs = computed(() => {
  let blogs = blogStore.blogs
  if (selectedCategory.value) {
    blogs = blogs.filter(blog => blog.category === selectedCategory.value)
  }
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    blogs = blogs.filter(blog => 
      blog.title.toLowerCase().includes(query) ||
      blog.summary.toLowerCase().includes(query) ||
      blog.content?.toLowerCase().includes(query) ||
      blog.category?.toLowerCase().includes(query)
    )
  }
  return displayedCount.value < blogs.length
})

const loadBlogs = async () => {
  await blogStore.fetchBlogs()
}

const handleSearch = () => {
  displayedCount.value = 9 // Reset to initial count
}

const setCategory = (category) => {
  selectedCategory.value = category
  displayedCount.value = 9 // Reset to initial count
}

const loadMore = () => {
  displayedCount.value += 6
}

const clearFilters = () => {
  searchQuery.value = ''
  selectedCategory.value = null
  displayedCount.value = 9
  sortBy.value = 'newest'
}

onMounted(() => {
  if (blogStore.blogs.length === 0) {
    loadBlogs()
  }
})
</script>



<style scoped>
.blog-page {
  min-height: 100vh;
  background: #f8fafc;
}

.blog-hero {
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  padding: 80px 0 60px;
  margin-bottom: 0;
  text-align: center !important;
}

.blog-hero .container,
.blog-hero .row,
.blog-hero .col-lg-8 {
  text-align: center !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
}

.blog-hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center !important;
  display: block !important;
  width: 100% !important;
  margin-left: -30px !important;
  margin-right: auto !important;
}

.blog-hero-subtitle {
  font-size: 2rem;
  opacity: 0.9;
  margin-bottom: 40px;
  text-align: center !important;
  display: block !important;
  width: 100% !important;
  margin-left: -150px !important;
  margin-right: auto !important;
  white-space: nowrap !important;
}

.search-container {
  max-width: 500px;
  margin: 0 auto;
  margin-left: -70px !important;
  text-align: center !important;
}

.search-container .input-group {
  display: inline-flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: auto !important;
}

.search-input {
  border: none;
  border-radius: 50px 0 0 50px;
  padding: 15px 25px;
  font-size: 1.1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: inline-block !important;
  float: none !important;
  width: auto !important;
}

.search-btn {
  border-radius: 0 50px 50px 0;
  padding: 15px 25px;
  border: none;
  background: #2563eb;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: inline-block !important;
  float: none !important;
  width: auto !important;
}

.search-btn:hover {
  background: #1e40af;
  color: white;
}

.category-filter {
  margin-bottom: 0;
  padding: 40px 0;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.category-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.category-btn {
  padding: 12px 24px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.95rem;
  min-width: 80px;
  text-align: center;
}

.category-btn:hover {
  border-color: #ff6b35;
  color: #ff6b35;
  background: #fef3c7;
  transform: translateY(-1px);
}

.category-btn.active {
  background: #ff6b35;
  border-color: #ff6b35;
  color: white;
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

.blog-content {
  padding: 60px 0;
  background: #f8fafc;
}

.blog-content .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Loading State */
.spinner-border {
  color: #ff6b35 !important;
}

.text-primary {
  color: #ff6b35 !important;
}

.btn-primary {
  background: #ff6b35 !important;
  border-color: #ff6b35 !important;
  color: white !important;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: #e55a2b !important;
  border-color: #e55a2b !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

.btn-outline-primary {
  border-color: #ff6b35 !important;
  color: #ff6b35 !important;
  background: transparent !important;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-outline-primary:hover {
  background: #ff6b35 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

/* Alert Styling */
.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
}

.alert-heading {
  color: #92400e;
  font-weight: 700;
  margin-bottom: 10px;
}

/* Blog Grid */
.row {
  margin: 0 -15px;
}

.col-lg-4, .col-md-6 {
  padding: 0 15px;
  margin-bottom: 30px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .blog-hero {
    padding: 60px 0 40px;
    min-height: 320px;
    text-align: center !important;
  }
  
  .blog-hero-title {
    font-size: 2.5rem;
    text-align: center !important;
    display: block !important;
    width: 100% !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  
  .blog-hero-subtitle {
    font-size: 1.1rem;
    margin-bottom: 30px;
    text-align: center !important;
    display: block !important;
    width: 100% !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  
  .search-container {
    max-width: 95%;
  }
  
  .search-input, .search-btn {
    font-size: 1rem;
    padding: 14px 20px;
  }
  
  .category-filter {
    padding: 30px 0;
  }
  
  .category-buttons {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 10px;
    gap: 8px;
  }
  
  .category-btn {
    padding: 10px 20px;
    font-size: 0.9rem;
    white-space: nowrap;
  }
  
  .blog-content {
    padding: 40px 0;
  }
  
  .col-lg-4, .col-md-6 {
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .blog-hero-title {
    font-size: 2rem;
  }
  
  .blog-hero-subtitle {
    font-size: 1rem;
  }
  
  .search-input, .search-btn {
    padding: 12px 16px;
    font-size: 0.95rem;
  }
  
  .category-btn {
    padding: 8px 16px;
    font-size: 0.85rem;
  }
}
</style>





<!-- <style scoped>
.blog-page {
  min-height: 100vh;
  background: #f8fafc;
}

.blog-hero {
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: white;
  padding: 80px 0 60px;
  margin-bottom: 0;
  text-align: center;
}

.blog-hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.blog-hero-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 40px;
}

.search-container {
  max-width: 500px;
  margin: 0 auto;
}

.search-input {
  border: none;
  border-radius: 50px 0 0 50px;
  padding: 15px 25px;
  font-size: 1.1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.search-btn {
  border-radius: 0 50px 50px 0;
  padding: 15px 25px;
  border: none;
  background: #2563eb;
  color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.search-btn:hover {
  background: #1e40af;
  color: white;
}

.category-filter {
  margin-bottom: 0;
  padding: 40px 0;
  border-bottom: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}

.category-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  align-items: center;
}

.category-btn {
  padding: 12px 24px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.95rem;
  min-width: 80px;
  text-align: center;
}

.category-btn:hover {
  border-color: #ff6b35;
  color: #ff6b35;
  background: #fef3c7;
  transform: translateY(-1px);
}

.category-btn.active {
  background: #ff6b35;
  border-color: #ff6b35;
  color: white;
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

.blog-content {
  padding: 60px 0;
  background: #f8fafc;
}

.blog-content .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Loading State */
.spinner-border {
  color: #ff6b35 !important;
}

.text-primary {
  color: #ff6b35 !important;
}

.btn-primary {
  background: #ff6b35 !important;
  border-color: #ff6b35 !important;
  color: white !important;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: #e55a2b !important;
  border-color: #e55a2b !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

.btn-outline-primary {
  border-color: #ff6b35 !important;
  color: #ff6b35 !important;
  background: transparent !important;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-outline-primary:hover {
  background: #ff6b35 !important;
  color: white !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,107,53,0.3);
}

/* Alert Styling */
.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
}

.alert-heading {
  color: #92400e;
  font-weight: 700;
  margin-bottom: 10px;
}

/* Blog Grid */
.row {
  margin: 0 -15px;
}

.col-lg-4, .col-md-6 {
  padding: 0 15px;
  margin-bottom: 30px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .blog-hero {
    padding: 60px 0 40px;
    min-height: 320px;
  }
  
  .blog-hero-title {
    font-size: 2.5rem;
  }
  
  .blog-hero-subtitle {
    font-size: 1.1rem;
    margin-bottom: 30px;
  }
  
  .search-container {
    max-width: 95%;
  }
  
  .search-input, .search-btn {
    font-size: 1rem;
    padding: 14px 20px;
  }
  
  .category-filter {
    padding: 30px 0;
  }
  
  .category-buttons {
    justify-content: flex-start;
    overflow-x: auto;
    padding-bottom: 10px;
    gap: 8px;
  }
  
  .category-btn {
    padding: 10px 20px;
    font-size: 0.9rem;
    white-space: nowrap;
  }
  
  .blog-content {
    padding: 40px 0;
  }
  
  .col-lg-4, .col-md-6 {
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .blog-hero-title {
    font-size: 2rem;
  }
  
  .blog-hero-subtitle {
    font-size: 1rem;
  }
  
  .search-input, .search-btn {
    padding: 12px 16px;
    font-size: 0.95rem;
  }
  
  .category-btn {
    padding: 8px 16px;
    font-size: 0.85rem;
  }
}
</style> -->
